/**
 * SpeedTrapAnimationOverlay - Cinematic "🛑 Speed Trap Radar" Animation Overlay
 *
 * Displays a theatrical cartoon police interception overlay when a player is stopped
 * by a Stop Sign or blocked in a No Passing Lane:
 * - Red & blue emergency strobe flashers illuminate the screen.
 * - An animated speed radar scope locks onto the offender's velocity.
 * - A motorcycle cop card with aviator sunglasses and police cap zips in on a chrome cruiser.
 * - Cop puts up a white-gloved hand ("HALT!") with a piercing police whistle.
 * - Slaps a bright yellow speeding citation directly onto the player's card face!
 */

let activeSpeedTrapInstance = null;

export class SpeedTrapAnimationOverlay {
  /**
   * Displays the Speed Trap Radar animation overlay.
   *
   * @param {Object} [options]
   * @param {string} [options.playerName] - Name of player stopped (e.g. "You", "The Shark")
   * @param {string} [options.who] - Seat ID ('p1', 'p2', etc.)
   * @param {string} [options.reason] - 'stop_sign' | 'no_passing_lane'
   * @param {number} [options.hole] - Hole number where vehicle was halted
   * @param {number} [options.ptsScored] - Points allowed through
   * @param {number} [options.ptsLost] - Points confiscated / lost
   * @param {string} [options.blockerName] - Name of vehicle blocking (if no passing lane)
   * @param {Object} [options.card] - Card object { r, s } representing the player's card
   * @param {Object} [options.settings] - Game settings object
   * @param {Function} [options.onComplete] - Callback on dismiss or completion
   * @returns {SpeedTrapAnimationOverlay|null}
   */
  static show(options = {}) {
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings) {
      const trapVal = settings.stopSignAnimation !== undefined ? settings.stopSignAnimation : (settings.stopAnimation !== undefined ? settings.stopAnimation : settings.speedTrapAnimation);
      if (trapVal === 'no' || trapVal === 'off' || trapVal === false) {
        if (typeof options.onComplete === 'function') options.onComplete();
        return null;
      }
    }

    if (typeof document === 'undefined') {
      if (typeof options.onComplete === 'function') options.onComplete();
      return null;
    }

    if (activeSpeedTrapInstance) {
      activeSpeedTrapInstance.close(false);
    }

    const instance = new SpeedTrapAnimationOverlay(options);
    activeSpeedTrapInstance = instance;
    instance.mount();
    return instance;
  }

  static hide() {
    if (activeSpeedTrapInstance) {
      activeSpeedTrapInstance.close(true);
    }
  }

  constructor(options = {}) {
    this.options = options;
    this.onComplete = options.onComplete || (() => {});
    this.overlayEl = null;
    this.autoCloseTimer = null;
    this.stepTimers = [];
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  formatCard(card, fallbackRank = 'J', fallbackSuit = '♠') {
    if (!card) {
      return { r: fallbackRank, s: fallbackSuit, isRed: fallbackSuit === '♥' || fallbackSuit === '♦' };
    }
    if (Array.isArray(card) && card.length > 0) {
      card = card[card.length - 1];
    }
    if (typeof card === 'string') {
      const match = card.match(/^([0-9AJQK]+)([HDSC♥♦♠♣]?)$/i);
      if (match) {
        card = { r: match[1], s: match[2] };
      }
    }
    let r = card.r !== undefined ? card.r : (card.rank !== undefined ? card.rank : fallbackRank);
    if (r === 1 || r === '1') r = 'A';
    else if (r === 11 || r === '11') r = 'J';
    else if (r === 12 || r === '12') r = 'Q';
    else if (r === 13 || r === '13') r = 'K';
    r = String(r).toUpperCase();

    let s = card.s || card.suit || fallbackSuit;
    if (s === 'H' || s === 'h') s = '♥';
    else if (s === 'D' || s === 'd') s = '♦';
    else if (s === 'S' || s === 's') s = '♠';
    else if (s === 'C' || s === 'c') s = '♣';

    const isRed = s === '♥' || s === '♦';
    return { r, s, isRed };
  }

  buildHtml() {
    const pName = this.options.playerName || 'Driver';
    const reason = this.options.reason || 'stop_sign';
    const hole = this.options.hole !== undefined ? this.options.hole : 35;
    const ptsScored = this.options.ptsScored !== undefined ? this.options.ptsScored : 2;
    const ptsLost = this.options.ptsLost !== undefined ? this.options.ptsLost : 4;
    const blockerName = this.options.blockerName || 'Lead Vehicle';
    const card = this.formatCard(this.options.card, 'K', '♠');

    const isLane = reason === 'no_passing_lane';
    const bannerTitle = isLane ? `${pName} Blocked in No Passing Lane!` : `🛑 Speed Trap: ${pName} Clocked at Stop Sign!`;
    const subTitle = isLane
      ? `Held Behind ${blockerName} at Hole ${hole} · Strict 15 MPH Zone`
      : `Stop Sign at Hole ${hole} · ${ptsLost > 0 ? `${ptsLost} Points Confiscated!` : 'Full Stop Enforced'}`;

    return `
      <!-- Red & Blue Emergency Strobe Flashers -->
      <div class="emergency-flasher-strobe" aria-hidden="true"></div>

      <!-- Skip Button -->
      <button type="button" class="speed-trap-skip-btn" id="speedTrapSkipBtn" aria-label="Skip Speed Trap Animation">
        Skip ✕
      </button>

      <!-- Header Banner & Police Radar Badge -->
      <div class="speed-trap-banner">
        <div class="police-badge-wrap">
          <span class="police-siren-pill"></span>
          <span>HIGHWAY PATROL · SPEED TRAP RADAR</span>
          <span class="police-siren-pill"></span>
        </div>
        <h2 class="speed-trap-title">${bannerTitle}</h2>
        <div class="speed-trap-subtitle">${subTitle}</div>
      </div>

      <!-- The Main Speed Trap Arena Stage -->
      <div class="speed-trap-stage" id="speedTrapStage">
        <!-- Radar Scope HUD -->
        <div class="radar-hud">
          <div class="radar-sweep-screen"></div>
          <div class="radar-data">
            <span class="radar-tag">RADAR LOCK ➔ HOLE ${hole}</span>
            <span class="radar-speed-val">STOPPED: ${ptsScored} PTS</span>
          </div>
        </div>

        <!-- Highway Asphalt Ground Floor -->
        <div class="highway-asphalt">
          <div class="dashed-highway-line"></div>
        </div>

        <!-- Offending Card (Left/Center Stage) -->
        <div class="violator-card-wrap sweating" id="violatorCardWrap">
          <span class="sweat-drop" aria-hidden="true">💧</span>
          <div class="violator-card ${card.isRed ? 'red' : 'black'}" id="violatorCard">
            <div class="corner tl">
              <span class="r">${card.r}</span>
              <span class="s">${card.s}</span>
            </div>
            <div class="card-rank-center">
              <span class="p-pip">${card.s}</span>
            </div>
            <div class="corner br">
              <span class="r">${card.r}</span>
              <span class="s">${card.s}</span>
            </div>
          </div>

          <!-- Impact Spikes from Ticket Slap -->
          <div class="slap-impact-lines" id="slapImpact"></div>

          <!-- Bright Yellow Speeding Citation Ticket Slapped On Card -->
          <div class="speeding-ticket" id="speedingTicket">
            <div class="ticket-header">TRAFFIC CITATION #CC-911</div>
            <div class="ticket-body">
              <div class="ticket-field"><span>VIOLATOR:</span><strong>${pName}</strong></div>
              <div class="ticket-field"><span>LOCATION:</span><strong>HOLE ${hole}</strong></div>
              <div class="ticket-field"><span>CHARGE:</span><strong>${isLane ? 'ILLEGAL PASSING' : 'FAILED TO STOP'}</strong></div>
              <div class="ticket-field"><span>PENALTY:</span><strong>${ptsLost > 0 ? `-${ptsLost} PTS LOST` : 'WARNING'}</strong></div>
            </div>
            <div class="red-rubber-stamp">🛑 CITATION ISSUED</div>
          </div>
        </div>

        <!-- Motorcycle Cop Card Container (Zips in from Right) -->
        <div class="motorcycle-cop-wrap" id="motorcycleCop">
          <!-- The Cruiser Motorcycle -->
          <div class="police-bike">
            <div class="bike-wheel front"><div class="hubcap"></div></div>
            <div class="bike-chassis">
              <div class="police-decal">PATROL</div>
            </div>
            <div class="exhaust-pipe"></div>
            <div class="bike-wheel rear"><div class="hubcap"></div></div>
            <div class="bike-siren-beacon"></div>
          </div>

          <!-- The Motorcycle Cop Playing Card -->
          <div class="cop-card">
            <!-- Police Cap with Gold Shield Badge -->
            <div class="police-cap">
              <div class="gold-badge"></div>
              <div class="cap-visor"></div>
            </div>

            <div class="cop-corner">👮</div>

            <!-- Aviator Sunglasses -->
            <div class="aviator-shades">
              <div class="lens"></div>
              <div class="bridge"></div>
              <div class="lens"></div>
            </div>
          </div>

          <!-- White-Gloved Arm Extended in "HALT!" Gesture -->
          <div class="halt-arm" id="copHaltArm">
            <div class="arm-sleeve"></div>
            <div class="white-glove">✋</div>
          </div>

          <!-- Comic "HALT!" Callout Bubble -->
          <div class="halt-bubble" id="copHaltBubble">HALT!</div>
        </div>

        <!-- Skid Tire Smoke Puffs -->
        <div class="bike-skid-smoke" id="bikeSmoke"></div>
      </div>
    `;
  }

  mount() {
    let el = document.getElementById('speedTrapAnimationOverlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'speedTrapAnimationOverlay';
      el.className = 'speed-trap-overlay';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Speed Trap Radar Animation');
      document.body.appendChild(el);
    }

    this.overlayEl = el;
    el.innerHTML = this.buildHtml();

    const skipBtn = el.querySelector('#speedTrapSkipBtn');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => this.close(true));
    }

    window.addEventListener('keydown', this.handleKeyDown);

    // Force layout flush before activating CSS transitions
    void el.offsetWidth;
    el.classList.add('active');

    this.startChoreography();
  }

  getAudio() {
    return (typeof window !== 'undefined' && (window.audio || window.audioManager || window.sfx || (window.CC && (window.CC.audio || window.CC.audioManager || window.CC.sfx)))) || null;
  }

  startChoreography() {
    const audio = this.getAudio();

    // 0.0s: Red & blue emergency flashers strobe, dual-tone cop siren wails
    if (audio && typeof audio.policeSirenWail === 'function') {
      audio.policeSirenWail();
    }

    // 0.4s: Motorcycle cop card revs up and zips in from right
    this.scheduleStep(400, () => {
      const cop = this.overlayEl?.querySelector('#motorcycleCop');
      const smoke = this.overlayEl?.querySelector('#bikeSmoke');
      if (cop) cop.classList.add('zipping-in');
      if (smoke) smoke.classList.add('active');

      if (audio && typeof audio.motorcycleRev === 'function') {
        audio.motorcycleRev();
      }
    });

    // 1.15s: Cop skids to a halt, puts up white-gloved hand ("HALT!") and blows whistle
    this.scheduleStep(1150, () => {
      const cop = this.overlayEl?.querySelector('#motorcycleCop');
      if (cop) cop.classList.add('halt-active');

      if (audio && typeof audio.policeWhistle === 'function') {
        audio.policeWhistle();
      }
    });

    // 1.85s: Cop card whips out ticket pad & WHACK! Slaps yellow citation onto card face
    this.scheduleStep(1850, () => {
      const ticket = this.overlayEl?.querySelector('#speedingTicket');
      const impact = this.overlayEl?.querySelector('#slapImpact');
      const card = this.overlayEl?.querySelector('#violatorCard');

      if (ticket) ticket.classList.add('slapped');
      if (impact) impact.classList.add('boom');
      if (card) {
        card.style.transform = 'scale(0.96) rotate(-2deg)';
        setTimeout(() => { if (card) card.style.transform = 'scale(1) rotate(0deg)'; }, 140);
      }

      if (audio && typeof audio.ticketSlap === 'function') {
        audio.ticketSlap();
      }
    });

    // 4.25s: Auto-close overlay gracefully
    this.autoCloseTimer = setTimeout(() => {
      this.close(true);
    }, 4250);
  }

  scheduleStep(delay, fn) {
    const t = setTimeout(fn, delay);
    this.stepTimers.push(t);
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc' || e.key === 'Enter' || e.key === ' ') {
      this.close(true);
    }
  }

  close(triggerCallback = true) {
    window.removeEventListener('keydown', this.handleKeyDown);

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    if (this.removeTimer) {
      clearTimeout(this.removeTimer);
      this.removeTimer = null;
    }
    this.stepTimers.forEach(t => clearTimeout(t));
    this.stepTimers = [];

    const el = this.overlayEl;
    this.overlayEl = null;

    if (activeSpeedTrapInstance === this) {
      activeSpeedTrapInstance = null;
    }

    if (el) {
      el.classList.remove('active');
      if (triggerCallback) {
        this.removeTimer = setTimeout(() => {
          if (el && el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }, 300);
      } else {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    }

    if (triggerCallback && typeof this.onComplete === 'function') {
      const cb = this.onComplete;
      this.onComplete = null;
      try {
        cb();
      } catch (err) {
        console.error('[SpeedTrapAnimationOverlay] onComplete error:', err);
      }
    }
  }
}
